/**
 * Anonymous session id — one per browser, sent on every gateway request as
 * X-Session-ID.
 *
 * This is the entire frontend half of session stitching. The id is generated
 * once and then just travels; the gateway does the rest. When it later sees this
 * header arrive alongside a valid JWT, it links the two and back-fills the
 * anonymous requests that came before — so the errors someone hit BEFORE
 * logging in are visible under their account afterwards.
 *
 * Deliberately NOT a user identifier: it is a random value with nothing derived
 * from the person, it is readable by anyone at the keyboard, and it is only ever
 * sent to our own gateway (see attachToFetch below).
 */

const SESSION_KEY = 'tpc_session_id'

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_URL || 'http://localhost:4000'

/**
 * crypto.randomUUID() exists only in a secure context (https, or localhost).
 * Anything else — a plain-http preview host, an old browser — leaves it
 * undefined, and an exception here would run on the very first API call the app
 * makes. The fallback is not cryptographically strong, and does not need to be:
 * this value only has to be unique enough to group one browser's requests.
 */
function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export function getSessionId(): string {
  // Safari in private mode, and any browser with storage disabled, throws on
  // localStorage access rather than returning null. A session id is a
  // nice-to-have for analytics, so failure degrades to a per-page-load id
  // instead of taking down every API call in the app.
  try {
    let id = localStorage.getItem(SESSION_KEY)
    if (!id) {
      id = newId()
      localStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch {
    return newId()
  }
}

/** Is this request going to our own gateway? */
function isGatewayRequest(url: string): boolean {
  try {
    // Relative URLs resolve against our own origin, which in dev is the same
    // host the gateway is proxied through.
    const target = new URL(url, window.location.href)
    return target.origin === new URL(GATEWAY_URL, window.location.href).origin
  } catch {
    return false
  }
}

/**
 * Add X-Session-ID to every fetch() aimed at the gateway.
 *
 * A wrapper rather than an edit to each call site: this app makes ~40 raw
 * fetch() calls across ~20 files with no shared header helper, and the axios
 * client below covers only a handful of them. Patching the transport is the one
 * place that catches all of it, and the one place to remove it from later.
 *
 * Scoped to the gateway origin on purpose — an unconditional header would hand
 * the id to every third party the app talks to (Meta Pixel, fonts, Cloudflare),
 * which is exactly the kind of quiet cross-site identifier it must not become.
 */
function attachToFetch(): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return

  const original = window.fetch.bind(window)

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const url =
        typeof input === 'string' ? input
        : input instanceof URL ? input.href
        : input.url

      if (!isGatewayRequest(url)) return original(input, init)

      // Headers() normalises every shape the caller may have used — a plain
      // object, an array of pairs, or an existing Headers instance — so nothing
      // the call site already set is lost.
      const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined))
      headers.set('X-Session-ID', getSessionId())

      return original(input, { ...init, headers })
    } catch {
      // Analytics must never break a request. Any surprise here falls through
      // to the untouched original fetch.
      return original(input, init)
    }
  }
}

let installed = false

/**
 * Call once at startup, before anything makes a request.
 * Guarded because a double-install would wrap the wrapper.
 */
export function installSessionTracking(): void {
  if (installed) return
  installed = true
  attachToFetch()
}
